import { Dialog } from '@base-ui/react/dialog';
import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * DESIGN.md › sheet (Figma "Title bar/sheet"): the sheet behind every picker — a bottom sheet
 * on a phone-sized window, a centred dialog from `md`. A Base UI Dialog underneath, so
 * focus, Escape and the backdrop are handled for free. Text arrives translated (`title`,
 * `closeLabel`) and so does the close glyph, since the kit ships no icons. Without `title`
 * the bar holds only the close button, and the content names the sheet with a `SheetTitle`.
 */
export function Sheet({
  open,
  title,
  closeLabel,
  closeIcon,
  onClose,
  children,
  className,
}: {
  open: boolean;
  /** The centred title in the bar; omit it to put a `SheetTitle` in the content instead. */
  title?: string;
  closeLabel: string;
  /** The glyph on the close button — the app passes its icon set's X. */
  closeIcon: ReactNode;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-surface-inverse/60" />
        <Dialog.Popup
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col rounded-t-lg bg-surface pb-section-sm font-brand text-on-surface outline-none md:inset-auto md:top-1/2 md:left-1/2 md:w-[480px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg',
            className,
          )}
        >
          <div className="flex h-14 items-center gap-content-sm px-content-sm">
            <Dialog.Close
              aria-label={closeLabel}
              className="flex size-10 cursor-pointer items-center justify-center rounded-full text-icon-strong hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight"
            >
              {closeIcon}
            </Dialog.Close>
            {title === undefined ? null : (
              <Dialog.Title className="flex-1 pr-10 text-center text-title-sm">
                {title}
              </Dialog.Title>
            )}
          </div>
          <div className="flex flex-col overflow-y-auto">{children}</div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The sheet's name inside its content, for a `Sheet` without a `title` (Figma title/Title LG). */
export function SheetTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Dialog.Title className={cn('text-center text-title-lg', className)}>{children}</Dialog.Title>
  );
}
